import { useCallback, useEffect, useRef, useState } from 'react'
import { Bot, Plug, Wrench } from 'lucide-react'
import { myAgentsAPI } from '../services/api'
import { PageHeader } from './cb/components/ui/PageHeader'
import { Panel } from './cb/components/ui/Panel'
import { TONE_CLASS } from './shell/agentTones'
import { MCP_ADDRESS_FAILED, useMcpAddress } from './cb/hooks/useMcpAddress'
import { shownText } from './cb/lib/agentSetupWords'
import { configForEditor } from './agentConfigText'
import ConnectAssistantSteps from './ConnectAssistantSteps'

/**
 * My agents: any member's own agents (registered by them, or declared with them as owner). Connect an assistant (the
 * assistant's own connect flow names or creates the agent), see each agent's status, disconnect one, or add a custom
 * agent (a token shown once). Admins see every company agent under Agents; this page is the member's own view.
 */

/** What a person sees, from the installation's authorization and its observed last call (kept separate, v2 §7). */
function statusOf(inst) {
  switch (inst?.authorizationStatus) {
    case 'authorized': return inst.lastCallAt ? { label: 'Connected', tone: 'ok' } : { label: 'Waiting for first call', tone: 'warn' }
    case 'revoked': return { label: 'Disconnected', tone: 'muted' }
    case 'retired': return { label: 'Retired', tone: 'muted' }
    default: return { label: 'Not signed in yet', tone: 'idle' }
  }
}
const PLATFORM = { claude: 'Claude', chatgpt: 'ChatGPT', custom: 'Custom agent' }

function when(iso) {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// §12.23 #4 — the NORMAL Add agent creates a HOSTED, reusable member-library profile (Claude or ChatGPT): it reuses (or
// creates) the member's single hosted installation for that assistant, so the profile joins the library the connected
// assistant discovers — no token, no second connector. A custom machine (its own static token, shown once) is a SEPARATE
// explicit choice; the credential kind is never inferred from a platform hint.
function CustomAgent({ onAdded }) {
  const HOSTED = [{ id: 'claude', label: 'Claude' }, { id: 'chatgpt', label: 'ChatGPT' }]
  const [name, setName] = useState('')
  const [platform, setPlatform] = useState('claude') // a hosted library profile by default
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [token, setToken] = useState(null)
  const [added, setAdded] = useState(null) // { platform } for a hosted add (no token)
  // The request key is generated ONCE per add and reused on retry, so an uncertain response (a dropped connection, a
  // timeout) followed by a retry returns the SAME agent instead of creating a duplicate. Editing name/platform resets it
  // (the key is bound to what was asked); a successful add clears it so the next agent gets a fresh key.
  const requestKeyRef = useRef(null)
  const mcp = useMcpAddress()
  const add = async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    if (!requestKeyRef.current) requestKeyRef.current = crypto.randomUUID()
    try {
      const r = await myAgentsAPI.register({ name: name.trim(), platform, requestKey: requestKeyRef.current })
      requestKeyRef.current = null
      if (platform === 'custom') setToken(r.setup?.token ?? null) // the token is shown once, here; ordinary reads never return it
      else { setToken(null); setAdded({ platform }) } // a hosted profile joins the library — no token
      onAdded()
    } catch (err) {
      // Keep requestKeyRef so a retry of the SAME add reuses the same key (no duplicate).
      setError(err?.body?.message || 'The agent could not be added. Try again, or use another name.')
    } finally { setBusy(false) }
  }
  if (token) {
    return (
      <div className="space-y-2 p-5 text-sm">
        <p className="font-semibold text-gray-900">Set these in your agent. The token is shown only once.</p>
        {mcp.url ? <code className="block break-all rounded-lg bg-gray-50 px-3 py-2 text-[13px]" data-mcp-url>{mcp.url}</code>
          : mcp.failed && <p role="alert" data-mcp-address-failed className="text-red-700">{MCP_ADDRESS_FAILED}</p>}
        <code className="block break-all rounded-lg bg-gray-50 px-3 py-2 text-[13px]" data-token>{token}</code>
        <p className="text-gray-600">Have it call Rithmo&apos;s <span className="font-mono text-[13px]">check_premises</span> before it acts.</p>
      </div>
    )
  }
  if (added) {
    const label = HOSTED.find((h) => h.id === added.platform)?.label || added.platform
    return (
      <div className="space-y-1 p-5 text-sm" data-add-hosted-note>
        <p className="font-semibold text-gray-900">Added to your {label} library.</p>
        <p className="text-gray-600">Connect {label} to Rithmo (or, if it is already connected, it appears on the assistant&apos;s next refresh) — no new sign-in is needed for another {label} agent.</p>
        <button type="button" onClick={() => setAdded(null)} className="mt-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Add another</button>
      </div>
    )
  }
  return (
    <form onSubmit={add} className="space-y-3 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Agent name" placeholder="Agent name" value={name} onChange={(e) => { setName(e.target.value); requestKeyRef.current = null }}
          className="min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-400" />
        <button type="submit" disabled={busy || name.trim().length < 2} className="rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">Add</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-add-platform role="radiogroup" aria-label="Where this agent runs">
        {HOSTED.map((h) => (
          <button key={h.id} type="button" role="radio" aria-checked={platform === h.id} data-add-platform-option={h.id}
            onClick={() => { setPlatform(h.id); requestKeyRef.current = null }}
            className={`rounded-full border px-3 py-1 font-semibold ${platform === h.id ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{h.label}</button>
        ))}
        <button type="button" role="radio" aria-checked={platform === 'custom'} data-add-platform-option="custom"
          onClick={() => { setPlatform('custom'); requestKeyRef.current = null }}
          className={`rounded-full border px-3 py-1 font-semibold ${platform === 'custom' ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>Custom machine (token)</button>
      </div>
      <p className="text-[11px] text-gray-500">{platform === 'custom'
        ? 'A custom machine gets its own one-time token. Use this for a server/script, not a hosted assistant.'
        : 'A hosted agent joins your library on that assistant — reuses your existing connection, no new sign-in for another agent.'}</p>
      {error && <p role="alert" className="w-full text-sm text-red-700">{error}</p>}
    </form>
  )
}

// The persistent enrollment bootstrap for ONE profile — the copyable prompt the member pastes into the host task, plus the
// honest host limitations. Fetched lazily on open.
function EnrollmentBlock({ agentId }) {
  const [state, setState] = useState({ status: 'loading' })
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    let alive = true
    myAgentsAPI.enrollment(agentId)
      .then((e) => { if (alive) setState({ status: 'ready', e }) })
      .catch((err) => { if (alive) setState({ status: err?.status === 503 ? 'no_address' : 'error' }) })
    return () => { alive = false }
  }, [agentId])
  if (state.status === 'loading') return <p className="text-xs text-gray-500" role="status">Loading enrollment…</p>
  if (state.status === 'no_address') return <p className="text-xs text-red-700" role="alert" data-mcp-address-failed>{MCP_ADDRESS_FAILED}</p>
  if (state.status === 'error') return <p className="text-xs text-red-700" role="alert">Could not load the enrollment instructions.</p>
  const copy = async () => { try { await navigator.clipboard.writeText(state.e.instructions); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* clipboard unavailable */ } }
  return (
    <div className="space-y-2" data-agent-enrollment>
      <p className="text-xs font-semibold text-gray-700">Enrollment — paste this into your {PLATFORM[state.e.platform] || state.e.platform} task&apos;s prompt</p>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-[12px] text-gray-800" data-enrollment-instructions>{state.e.instructions}</pre>
      <button type="button" onClick={copy} data-enrollment-copy className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">{copied ? 'Copied' : 'Copy'}</button>
      {state.e.hostLimitations?.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-[11px] text-gray-500" data-enrollment-limits>
          {state.e.hostLimitations.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
    </div>
  )
}

// IR5 — the OWNER configures their OWN agent: its instructions (the operating prompt Rithmo serves it) and a short purpose,
// plus the per-profile enrollment readback. Loads the current config, saves via the owner-principal route, and shows the
// NEW revision so the save is visibly confirmed (optimistic-revision UI). A save carries the loaded revision; if a
// concurrent edit moved it the save is REFUSED (409) — the editor keeps the unsaved input and offers Reload-and-reapply.
// Owner-only is enforced server-side; a non-owner never sees this control.
function ConfigureAgent({ agentId }) {
  const [status, setStatus] = useState('loading')
  const [instructions, setInstructions] = useState('')
  // The instructions as last loaded or saved: a save sends them only when the owner changed them, so saving the purpose or
  // declarations never rewrites them. Instructions an org admin set or cleared are read-only here (the server refuses them).
  const [savedInstructions, setSavedInstructions] = useState('')
  const [instructionsEditable, setInstructionsEditable] = useState(true)
  const [purpose, setPurpose] = useState('')
  // §12.16 item 3 — the member's OWN tool/action DECLARATIONS (descriptive intent; grants nothing). One per line; a tool is
  // `key` or `key: note`, an action is `key: what it intends to do`. The server validates the keys and refuses any
  // authority-bearing field, so this never grants access or makes a governed action unchecked.
  const [declTools, setDeclTools] = useState('')
  const [declActions, setDeclActions] = useState('')
  const [rev, setRev] = useState(null)
  const [readback, setReadback] = useState({ documents: [], tools: [], actions: [] })
  const [management, setManagement] = useState(false)
  const [mgmtBusy, setMgmtBusy] = useState(false)
  const [mgmtNote, setMgmtNote] = useState(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)
  const [conflict, setConflict] = useState(false)
  useEffect(() => {
    let alive = true
    myAgentsAPI.getConfig(agentId)
      .then((c) => {
        if (!alive) return
        const e = configForEditor(c) // the text as typed: the API returns it entity-encoded
        setInstructions(e.instructions); setSavedInstructions(e.instructions); setInstructionsEditable(c.instructionsEditable !== false)
        setPurpose(e.purpose); setRev(c.configRevision)
        setReadback({ documents: c.documents || [], tools: c.tools || [], actions: c.actions || [] })
        setDeclTools(e.declTools)
        setDeclActions(e.declActions)
        setManagement(!!c.managementEnabled); setStatus('ready')
      })
      .catch(() => { if (alive) setStatus('error') })
    return () => { alive = false }
  }, [agentId])
  const toggleManagement = async () => {
    const next = !management
    setMgmtBusy(true); setMgmtNote(null)
    try {
      const r = await myAgentsAPI.setManagement(agentId, next)
      setManagement(r.managementEnabled)
      setMgmtNote(r.reconnectRequired
        ? { tone: 'ok', text: 'Allowed. Reconnect this assistant so its next sign-in grants the manage permission.' }
        : { tone: 'ok', text: 'Self-management turned off. The agent can no longer manage itself.' })
    } catch (err) {
      setMgmtNote({ tone: 'alert', text: err?.body?.message || 'Could not change the permission. Try again.' })
    } finally { setMgmtBusy(false) }
  }
  // Parse a declarations textarea (one entry per line; `key` or `key: free text`) into the bounded descriptive shape. The
  // server is the authority — it validates the keys and refuses any authority-bearing field — so this stays lenient.
  const parseDecl = (text, valueField) => text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf(':')
    if (i < 0) return { key: l }
    const value = l.slice(i + 1).trim()
    return value ? { key: l.slice(0, i).trim(), [valueField]: value } : { key: l.slice(0, i).trim() }
  })
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setNote(null); setConflict(false)
    try {
      const tools = parseDecl(declTools, 'note')
      const actions = parseDecl(declActions, 'intended_action')
      const declarations = tools.length || actions.length ? { tools, actions } : null
      const changed = instructionsEditable && instructions.trim() !== savedInstructions.trim()
      const c = await myAgentsAPI.updateConfig(agentId, { ...(changed ? { instructions } : {}), purpose, declarations, expectedRevision: rev })
      setRev(c.configRevision) // the revision moves on save — the UI shows the agent now runs from this configuration
      const saved = configForEditor(c).instructions; setSavedInstructions(saved); setInstructions(saved)
      setNote({ tone: 'ok', text: 'Saved. Your agent uses this from its next run.' })
    } catch (err) {
      if (err?.status === 409) {
        // §12.14 — someone else changed the config since we loaded it. Keep the member's unsaved input; let them Reload to
        // pick up the latest revision and Save again (re-apply) — we never silently overwrite the other edit.
        setConflict(true)
        setNote({ tone: 'alert', text: 'Someone else changed this agent’s configuration. Reload to pick up their changes, then Save again to re-apply yours.' })
      } else if (err?.status === 403 && err?.body?.error === 'admin_set') {
        // An org admin took over the instructions since we loaded them: show theirs, read-only. Nothing was saved.
        setInstructionsEditable(false); setInstructions(savedInstructions)
        setNote({ tone: 'alert', text: err.body.message || 'An org admin set these instructions. Nothing was saved.' })
      } else {
        setNote({ tone: 'alert', text: err?.body?.message || 'Could not save. Try again.' })
      }
    } finally { setBusy(false) }
  }
  // Reload the latest revision WITHOUT discarding the member's unsaved edits, so Save re-applies them over the latest.
  const reload = async () => {
    setBusy(true)
    try {
      const c = await myAgentsAPI.getConfig(agentId); setRev(c.configRevision); setConflict(false)
      // Keep the owner's own instruction edits to re-apply; untouched (or admin-controlled) instructions take the latest, so
      // a later save never writes back stale text.
      const latest = configForEditor(c).instructions
      if (c.instructionsEditable === false || instructions.trim() === savedInstructions.trim()) setInstructions(latest)
      setSavedInstructions(latest); setInstructionsEditable(c.instructionsEditable !== false)
      setNote({ tone: 'ok', text: 'Reloaded. Your edits are kept — Save again to apply them over the latest.' }) }
    catch { setNote({ tone: 'alert', text: 'Could not reload. Try again.' }) }
    finally { setBusy(false) }
  }
  if (status === 'loading') return <p className="px-5 pb-4 text-xs text-gray-500" role="status">Loading configuration…</p>
  if (status === 'error') return <p className="px-5 pb-4 text-xs text-red-700" role="alert">Could not load this agent&apos;s configuration.</p>
  return (
    <div className="mt-3 space-y-4 border-t border-gray-100 pt-3">
      <form onSubmit={save} className="space-y-3" data-agent-config>
        <label className="block text-xs font-semibold text-gray-700">Instructions
          <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={4} placeholder="How this agent should operate…"
            readOnly={!instructionsEditable} aria-readonly={!instructionsEditable}
            data-config-instructions className={`mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary-400 ${instructionsEditable ? '' : 'bg-gray-50 text-gray-600'}`} />
        </label>
        {!instructionsEditable && (
          <p className="text-[11px] text-gray-500" data-config-instructions-admin>An org admin set these instructions. Only an admin can change them, on the Control Board; you can still edit the purpose and declarations.</p>
        )}
        <label className="block text-xs font-semibold text-gray-700">Purpose
          <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="A short description"
            data-config-purpose className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary-400" />
        </label>
        {/* §12.16 item 3 — the member's OWN tool/action declarations. Descriptive intent only: it never grants tool or
            source access, cannot make a governed action unchecked, and does not change your company's readiness or sources.
            One per line — a tool is `key` or `key: note`; an action is `key: what it intends to do`. */}
        <label className="block text-xs font-semibold text-gray-700">Tools this agent intends to use
          <textarea value={declTools} onChange={(e) => setDeclTools(e.target.value)} rows={2} placeholder={'gmail: send summaries\nslack'}
            data-config-decl-tools className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary-400" />
        </label>
        <label className="block text-xs font-semibold text-gray-700">Actions this agent intends to take
          <textarea value={declActions} onChange={(e) => setDeclActions(e.target.value)} rows={2} placeholder={'email_standup: email the standup recap'}
            data-config-decl-actions className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary-400" />
          <span className="mt-1 block text-[11px] font-normal text-gray-500">A declaration describes intent. It grants nothing — your company still governs which actions must be checked with Rithmo.</span>
        </label>
        {note && <p role="status" className={`text-xs ${note.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`} data-config-note>{note.text}</p>}
        {rev && <p className="text-[11px] text-gray-400" data-config-rev>Configuration revision {String(rev).slice(0, 12)}</p>}
        {readback.actions.length > 0 && (
          // Read-only readback — the governed actions your org declared this agent must check with Rithmo before taking.
          // A member cannot edit these (they are the org's policy/source authority); shown for transparency.
          <div className="rounded-lg bg-gray-50 px-3 py-2" data-config-readback>
            <p className="text-[11px] font-semibold text-gray-600">Governed actions (set by your company — read-only)</p>
            <ul className="mt-1 space-y-0.5 text-[11px] text-gray-500">
              {readback.actions.map((a) => <li key={a.key}><span className="font-mono">{shownText(a.intended_action || '')}</span>{a.check_with_rithmo ? ' · checks with Rithmo' : ''}</li>)}
            </ul>
          </div>
        )}
        <div className="flex gap-2">
          <button type="submit" disabled={busy} data-config-save
            className="rounded-lg bg-primary-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-60">{busy ? 'Saving…' : 'Save configuration'}</button>
          {conflict && (
            <button type="button" onClick={reload} disabled={busy} data-config-reload
              className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-60">Reload latest</button>
          )}
        </div>
      </form>
      {/* §12.14 — the owner opts the agent into self-management (mcp:agents:write). Enabling only raises the ceiling; a
          reconnect binds a grant that carries the permission. Turning it off denies the capability immediately. */}
      <div className="space-y-1" data-agent-management>
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
          <input type="checkbox" checked={management} disabled={mgmtBusy} onChange={toggleManagement} data-management-toggle className="h-3.5 w-3.5" />
          Let this agent add profiles and set their purpose (mcp:agents:write)
        </label>
        <p className="text-[11px] text-gray-500">The agent may call register_my_agent and update_agent over Rithmo. It can never change instructions (you set them here, or an org admin does), another person’s agents, your policy, or your sources.</p>
        {mgmtNote && <p role="status" className={`text-[11px] ${mgmtNote.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`} data-management-note>{mgmtNote.text}</p>}
      </div>
      <EnrollmentBlock agentId={agentId} />
    </div>
  )
}

export default function CustomerMyAgentsPage() {
  const [state, setState] = useState({ status: 'loading', agents: [], installations: {} })
  const [note, setNote] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [showCustom, setShowCustom] = useState(false)
  const [editingId, setEditingId] = useState(null) // the agentId whose IR5 config editor is open (one at a time)

  const load = useCallback(async () => {
    try {
      const r = await myAgentsAPI.list()
      setState({ status: 'ready', agents: r.agents || [], installations: Object.fromEntries((r.installations || []).map((i) => [i.installationId, i])) })
    } catch {
      setState((s) => ({ ...s, status: 'error' }))
    }
  }, [])
  useEffect(() => { load() }, [load])

  // Installation-wide (v2 §6). Always refresh afterwards and say truthfully what happened, including cleanup still pending.
  const disconnect = async (a) => {
    setBusyId(a.installationId); setNote(null)
    try {
      const r = await myAgentsAPI.disconnectInstallation(a.installationId)
      setNote(r.providerCleanup?.pending
        ? { tone: 'warn', text: `${shownText(a.name || '')} is disconnected from Rithmo. Removing it at the assistant is still finishing.` }
        : { tone: 'ok', text: `${shownText(a.name || '')} is disconnected.` })
    } catch {
      setNote({ tone: 'alert', text: `Could not finish disconnecting ${shownText(a.name || '')}. Its current state is shown below; try again.` })
    } finally { setBusyId(null); load() }
  }

  // Profile-level: retire ONE agent; peers on the same installation keep working (distinct from Disconnect, which is
  // installation-wide).
  const retire = async (a) => {
    setBusyId(a.agentId); setNote(null)
    try {
      await myAgentsAPI.retireProfile(a.agentId)
      setNote({ tone: 'ok', text: `${shownText(a.name || '')} is retired. Your other agents on this connection keep working.` })
    } catch {
      setNote({ tone: 'alert', text: `Could not retire ${shownText(a.name || '')}. Try again.` })
    } finally { setBusyId(null); load() }
  }

  const { agents } = state
  return (
    <>
      <PageHeader Icon={Bot} title="My agents" description="The AI assistants and agents you connected to Rithmo." />
      {note && <div role="status" className={`mb-5 rounded-xl border p-3 text-sm ${TONE_CLASS[note.tone]}`}>{note.text}</div>}
      <div className="space-y-5">
        <Panel Icon={Plug} title={agents.length ? 'Connect another assistant' : 'Connect your AI assistant'}>
          <div className="p-5"><ConnectAssistantSteps /></div>
        </Panel>

        <Panel Icon={Bot} title="Your agents">
          {state.status === 'loading' && <p className="p-5 text-sm text-gray-500" role="status">Loading…</p>}
          {state.status === 'error' && <p className="p-5 text-sm text-red-700" role="alert">Your agents could not be loaded. Refresh to try again.</p>}
          {state.status === 'ready' && agents.length === 0 && (
            <p className="p-5 text-sm text-gray-500">None yet. Connect an assistant above and it appears here.</p>
          )}
          {agents.length > 0 && (
            <ul className="divide-y divide-gray-200" data-my-agents>
              {agents.map((a) => {
                const inst = state.installations[a.installationId]
                // A profile can be retired on its own while the installation stays authorized for its peers.
                const s = a.status === 'retired' ? { label: 'Retired', tone: 'muted' } : statusOf(inst)
                const live = a.status !== 'retired' && inst?.authorizationStatus === 'authorized'
                const owned = a.agentId && a.ownedByMe !== false // an owner-only control; the server also enforces it
                return (
                  <li key={a.agentId || a.key || a.installationId} className="px-5 py-4" data-agent-row={a.agentId || undefined}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm font-semibold text-gray-900">{shownText(a.name || '')}</p>
                        <p className="text-xs text-gray-500">{PLATFORM[a.platform] || a.platform}{when(inst?.lastCallAt) ? ` · Last call ${when(inst.lastCallAt)}` : ''}</p>
                      </div>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASS[s.tone]}`}>{s.label}</span>
                      {owned && a.status !== 'retired' && (
                        // IR5 — the owner configures THIS agent's instructions / purpose (owner-principal enforced server-side).
                        <button type="button" onClick={() => setEditingId((id) => (id === a.agentId ? null : a.agentId))}
                          data-config-toggle className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                          {editingId === a.agentId ? 'Close' : 'Configure'}
                        </button>
                      )}
                      {a.agentId && a.status !== 'retired' && (
                        // Profile-level revoke: retire THIS agent; the installation + other agents keep working.
                        <button type="button" onClick={() => retire(a)} disabled={busyId === a.agentId}
                          data-retire-profile className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                          {busyId === a.agentId ? 'Retiring…' : 'Retire'}
                        </button>
                      )}
                      {live && (
                        <button type="button" onClick={() => disconnect(a)} disabled={busyId === a.installationId}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                          {busyId === a.installationId ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      )}
                      {!live && inst?.cleanupPending && (
                        // Provider cleanup from an earlier disconnect is still finishing — re-driving the disconnect
                        // continues it from durable state. Stays available after the agent is revoked/retired.
                        <button type="button" onClick={() => disconnect(a)} disabled={busyId === a.installationId}
                          data-cleanup-retry className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-60">
                          {busyId === a.installationId ? 'Finishing…' : 'Finish disconnecting'}
                        </button>
                      )}
                    </div>
                    {owned && a.status !== 'retired' && editingId === a.agentId && <ConfigureAgent agentId={a.agentId} />}
                  </li>
                )
              })}
            </ul>
          )}
        </Panel>

        <Panel Icon={Wrench} title="Custom agent" aside={!showCustom && (
          <button type="button" onClick={() => setShowCustom(true)} className="text-sm font-semibold text-primary-600 hover:text-primary-700">Add one</button>
        )}>
          {showCustom ? <CustomAgent onAdded={load} /> : <p className="p-5 text-sm text-gray-500">Built your own agent? Add it to get its access token.</p>}
        </Panel>
      </div>
    </>
  )
}
